import React, { useMemo } from 'react';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const SALUTATIONS = ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'M/s', 'Master'];
const RELATIONS = ['Son of', 'Daughter of', 'Wife of', 'Husband of'];

/**
 * Parses a stored person string back into structured pieces.
 * Stored format examples:
 *   "Mr. Darshan"
 *   "Mrs. Lakshmi Wife of Mr. Ramesh"
 */
function parsePerson(value) {
  const text = (value || '').trim();
  if (!text) return { sal: '', name: '', rel: '', relSal: '', relName: '' };
  const relRegex = /\s+(Son of|Daughter of|Wife of)\s+/i;
  const relMatch = text.match(relRegex);
  if (relMatch) {
    const left = text.slice(0, relMatch.index).trim();
    const right = text.slice(relMatch.index + relMatch[0].length).trim();
    return {
      ...splitSalName(left),
      rel: relMatch[1],
      relSal: splitSalName(right).sal,
      relName: splitSalName(right).name,
    };
  }
  return { ...splitSalName(text), rel: '', relSal: '', relName: '' };
}

function splitSalName(s) {
  const t = s.trim();
  for (const sal of SALUTATIONS) {
    if (t.startsWith(sal + ' ')) return { sal, name: t.slice(sal.length + 1).trim() };
    if (t === sal) return { sal, name: '' };
  }
  return { sal: '', name: t };
}

function buildPerson({ sal, name, rel, relSal, relName }, withRelation) {
  const left = [sal, name].filter(Boolean).join(' ').trim();
  if (!withRelation) return left;
  const right = [relSal, relName].filter(Boolean).join(' ').trim();
  if (!rel || (!right && !left)) return left;
  return [left, rel, right].filter(Boolean).join(' ').trim();
}

export const PersonField = ({ value, onChange, label, withRelation = false, testId }) => {
  const parsed = useMemo(() => parsePerson(value), [value]);
  const update = (patch) => {
    const merged = { ...parsed, ...patch };
    onChange(buildPerson(merged, withRelation));
  };

  return (
    <div className="space-y-2">
      {label && <span className="text-sm font-medium text-slate-700">{label}</span>}
      <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-2 items-center">
        <Select
          value={parsed.sal}
          onValueChange={(v) => update({ sal: v })}
        >
          <SelectTrigger data-testid={testId ? `${testId}-sal` : undefined}>
            <SelectValue placeholder="Salutation" />
          </SelectTrigger>
          <SelectContent>
            {SALUTATIONS.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={parsed.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="Name"
          data-testid={testId ? `${testId}-name` : undefined}
        />
      </div>

      {withRelation && (
        <div className="grid grid-cols-1 sm:grid-cols-[150px_110px_1fr] gap-2 items-center">
          <Select
            value={parsed.rel}
            onValueChange={(v) => update({ rel: v })}
          >
            <SelectTrigger data-testid={testId ? `${testId}-rel` : undefined}>
              <SelectValue placeholder="Relation" />
            </SelectTrigger>
            <SelectContent>
              {RELATIONS.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={parsed.relSal}
            onValueChange={(v) => update({ relSal: v })}
          >
            <SelectTrigger data-testid={testId ? `${testId}-relsal` : undefined}>
              <SelectValue placeholder="Salutation" />
            </SelectTrigger>
            <SelectContent>
              {SALUTATIONS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={parsed.relName}
            onChange={(e) => update({ relName: e.target.value })}
            placeholder="Related person name"
            data-testid={testId ? `${testId}-relname` : undefined}
          />
        </div>
      )}

      {value && (
        <p className="text-xs text-slate-400 italic">
          Will render as: <span className="font-medium text-slate-600">{value}</span>
        </p>
      )}
    </div>
  );
};

export default PersonField;
